import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NewCheckbox } from '../../../../../Components/PIM/common';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import { deriveDesignStatusKey, hasStatusComment } from '../../../DesignDetail/utils/deriveDesignStatus';
import { DownloadIcon, CareUserInstructionsIcon, NoimageIcon } from '../../../../../bombiix-icon';
import { showBigImage, hideImage } from '../../../../../Components/helper';
import type { BulkAgRow, BulkGridCanvas, BulkGridHandlers } from '../types';

/**
 * AG Grid cell-renderer props, narrowed to the pieces the bulk cells read.
 * BombiixAGGrid spreads a column's `cellProps` straight onto the renderer, so
 * the handlers and canvasIndex arrive as props rather than via colDef.
 */
export type BulkCellParams = {
  data?: BulkAgRow;
  value?: unknown;
  canvasIndex?: number;
} & Partial<BulkGridHandlers>;

/** Resolve the renderable thumbnail source for one image slot (annotated preferred). */
const slotSrc = (image?: { annotedImage?: string; image?: string }): string => image?.annotedImage || image?.image || '';

/**
 * The original upload for a slot, used for the hover preview so the enlarge shows the full
 * original rather than the (annotated) thumbnail the strip renders. Falls back to the thumb
 * source when there is no separate original.
 */
const slotFullSrc = (image?: { annotedImage?: string; image?: string; imageFull?: string }): string =>
  image?.imageFull || image?.image || image?.annotedImage || '';

/**
 * Max image slots rendered per canvas cell. The strip is `overflow: hidden` and only
 * fits this many, so rendering more just downloads + decodes full-size images the user
 * never sees. Matches CANVAS_SLOTS in DesignBulkAgGrid.
 */
const MAX_VISIBLE_SLOTS = 6;
/** Rendered pixel size of a thumbnail slot (mirrors .design-bulk-ag__thumb in CSS). */
const SLOT_PX = 50;

/**
 * Memoised thumbnail `<img>`, keyed on `src` (the Samples-todo AG Grid image fix). AG Grid
 * re-runs a cell's renderer on every selection / model refresh; without memoisation each
 * refresh recreates the `<img>` and the browser re-fetches + re-decodes every visible
 * thumbnail, stalling scroll. Memoising on `src` (with a stable `onBroken`) keeps the element
 * mounted, so only a genuine src change reloads it. `loading="lazy"` still bounds the initial
 * fetch to on-screen rows.
 */
const ThumbImage = memo(function ThumbImage({ src, fullSrc, onBroken }: { src: string; fullSrc?: string | undefined; onBroken: () => void }) {
  return (
    <div className="image-detail hover-right">
      <figure className="design-bulk-ag__thumb-figure">
        <img
          src={src}
          data-full-src={fullSrc || src}
          alt="design"
          className="design-bulk-ag__thumb"
          width={SLOT_PX}
          height={SLOT_PX}
          loading="lazy"
          decoding="async"
          onMouseOver={showBigImage}
          onFocus={showBigImage}
          onMouseLeave={hideImage}
          onBlur={hideImage}
          onError={onBroken}
        />
      </figure>
    </div>
  );
});

/**
 * One image square inside a canvas cell: renders the memoised thumbnail, or the
 * no-image placeholder used by the samples listing page when no image has been
 * uploaded. The placeholder stays a button because it is the only upload
 * affordance for an empty slot.
 * A broken/expired presigned URL falls back to the same placeholder.
 */
const ImageSlot: React.FC<{ src: string; fullSrc?: string | undefined; onPick: (_file: File) => void }> = ({ src, fullSrc }) => {
  const [broken, setBroken] = useState(false);
  /** A new src (row reused on scroll, or a fresh upload) clears any previous load error. */
  useEffect(() => setBroken(false), [src]);
  const handleBroken = useCallback(() => setBroken(true), []);

  if (src && !broken) {
    return <ThumbImage src={src} fullSrc={fullSrc} onBroken={handleBroken} />;
  }
  return (
    <div className="image-detail hover-right sample-image-cell">
      <figure className="sample-image-cell__figure">
        <span className="sample-image-cell__placeholder">
          <NoimageIcon size={24} />
        </span>
      </figure>
    </div>
  );
};

/** Header-component params for the pinned select column. */
export interface BulkSelectHeaderParams {
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleAll?: () => void;
}

/**
 * Select-all checkbox rendered inside the grid header of the 40px select column,
 * where the Figma frame places it — not as a separate control above the table.
 *
 * State arrives directly through `headerComponentParams`: AG Grid's reactive custom
 * components re-render the header whenever those params change, which is the
 * documented update path (the same pattern as the Costing to-do ExpandAllHeader).
 * A ref + manual-subscription variant was tried before and left the checkbox stale.
 */
export const SelectAllHeader: React.FC<BulkSelectHeaderParams> = ({ allSelected, someSelected, onToggleAll }) => {
  const { t } = useTranslation('bulk-edit');
  return (
    <span className="design-bulk-ag__check">
      <NewCheckbox
        checked={Boolean(allSelected)}
        indeterminate={Boolean(someSelected) && !allSelected}
        onChange={() => onToggleAll?.()}
        aria-label={t('SELECT_ALL_FOR_APPROVAL')}
      />
    </span>
  );
};

/** Row-level checkbox: selects/deselects every canvas in the product row. */
export const RowSelectCell: React.FC<BulkCellParams> = ({ data, onToggleRow }) => {
  if (!data) {
    return null;
  }
  return (
    <span className="design-bulk-ag__check">
      <NewCheckbox
        checked={data.rowSelected}
        disabled={data.canvases.length === 0}
        onChange={() => onToggleRow?.(data.devOrderId)}
        aria-label={data.productNumber}
      />
    </span>
  );
};

/** Product number + name cell, with a redirect action to the product's Design Details page. */
export const ProductNumberCell: React.FC<BulkCellParams> = ({ data, onOpenProduct }) => {
  const { t } = useTranslation('bulk-edit');
  if (!data) {
    return null;
  }
  return (
    <div className="design-bulk-ag__product">
      <div className="design-bulk-ag__product-text">
        <CustomTooltip placement="top" tooltipText={data.productNumber || '-'}>
          {/* The number itself is the link to Design Details — the separate redirect icon was
              dropped per the client review, so the action moves onto the number (bold + underlined,
              matching the product card in Figma). */}
          <button
            type="button"
            className="design-bulk-ag__product-number"
            onClick={() => onOpenProduct?.(data.devOrderId)}
            aria-label={t('OPEN_DESIGN_DETAILS')}
          >
            {data.productNumber || '—'}
          </button>
        </CustomTooltip>
      </div>
    </div>
  );
};

/**
 * One canvas cell: a select checkbox, a horizontal strip of design-image
 * thumbnails, and an edit-comment icon. Reads its canvas from the row via the
 * column's `canvasIndex`; selection + comment state are folded into the row.
 */
export const CanvasCell: React.FC<BulkCellParams> = ({ data, canvasIndex = 0, onToggleCanvas, onEditComment, onUploadSlot }) => {
  const canvas: BulkGridCanvas | undefined = data?.canvases?.[canvasIndex];
  if (!data || !canvas) {
    return <div className="design-bulk-ag__cell design-bulk-ag__cell--empty" />;
  }

  const selected = data.selectedCanvasIds.includes(canvas.designId);
  const comment = data.canvasComments[canvas.designId] ?? canvas.pdfExcludedComment;
  const statusKey = deriveDesignStatusKey(canvas.status);
  const commentText = comment
    ? String(comment)
        .replace(/<[^>]*>/g, ' ')
        .trim()
    : '';

  return (
    <div className="design-bulk-ag__cell">
      <span className="design-bulk-ag__check">
        <NewCheckbox checked={selected} onChange={() => onToggleCanvas?.(data.devOrderId, canvas.designId)} aria-label={canvas.name} />
      </span>
      <div className="design-bulk-ag__thumbs">
        {Array.from({ length: Math.min(MAX_VISIBLE_SLOTS, Math.max(canvas.numberOfImages || 1, canvas.images?.length || 0)) }).map((_, slotIndex) => (
          <ImageSlot
            key={canvas.images?.[slotIndex]?._id ?? `${canvas.designId}-${slotIndex}`}
            src={slotSrc(canvas.images?.[slotIndex])}
            fullSrc={slotFullSrc(canvas.images?.[slotIndex])}
            onPick={(file) => onUploadSlot?.(data.devOrderId, canvasIndex, canvas.designId, slotIndex, file)}
          />
        ))}
      </div>
      {statusKey && (
        <CustomTooltip placement="top" tooltipText={commentText || '-'}>
          <button
            type="button"
            className={`bombiix-icon-container design-bulk-ag__comment${hasStatusComment(comment) ? ' ' : ''}`}
            onClick={(event) => onEditComment?.(data.devOrderId, canvas, event.currentTarget)}
            aria-label={canvas.name}
          >
            <CareUserInstructionsIcon size={24} />
          </button>
        </CustomTooltip>
      )}
    </div>
  );
};

/** Trailing per-row download action. */
export const DownloadCell: React.FC<BulkCellParams> = ({ data, onDownloadRow }) => {
  if (!data) {
    return null;
  }
  return (
    <button
      type="button"
      className="bombiix-icon-container design-bulk-ag__download"
      onClick={() => onDownloadRow?.(data.devOrderId)}
      aria-label={data.productNumber}
    >
      <DownloadIcon size={24} />
    </button>
  );
};
